import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Box, Button } from '@mui/material';
import { IconButton } from '@mui/material';
import { Dialog, DialogContent, DialogTitle, DialogActions } from '@mui/material';
import { TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import RoomIcon from '@mui/icons-material/Room';
import MapPopupInfo from 'components/Maps';
import IntlMessages, { SimpleText } from 'util/IntlMessages';
import styles from './ShelterListManager.module.scss';

const shelterShape = PropTypes.shape({
  location: PropTypes.string.isRequired,
  coords: PropTypes.arrayOf(PropTypes.number),
  address: PropTypes.string,
  placeId: PropTypes.string,
});

class ShelterListManager extends Component {
  static propTypes = {
    value: PropTypes.arrayOf(shelterShape).isRequired,
    onChange: PropTypes.func.isRequired,
    // eslint-disable-next-line react/no-unused-prop-types
    disabled: PropTypes.bool, // reserved for forward-compat (parent may use stacking layout in task 06)
  };

  static defaultProps = {
    disabled: false,
  };

  state = {
    isDialogOpen: false,     // whether the picker dialog is showing
    pendingSelection: null,  // { name, address, lat, lng, placeId } | null
    editingIndex: null,      // number | null
    duplicateError: null,    // string | null — name that collided on confirm
  };

  openAdd = () => {
    this.setState({
      isDialogOpen: true,
      pendingSelection: null,
      editingIndex: null,
      duplicateError: null,
    });
  };

  openEdit = index => () => {
    const row = this.props.value[index];
    if (!row) return;
    const [lat, lng] = Array.isArray(row.coords) && row.coords.length === 2 ? row.coords : [];
    this.setState({
      isDialogOpen: true,
      pendingSelection: {
        name: row.location,
        address: row.address || '',
        lat,
        lng,
        placeId: row.placeId,
      },
      editingIndex: index,
      duplicateError: null,
    });
  };

  closePicker = () => {
    this.setState({
      isDialogOpen: false,
      pendingSelection: null,
      editingIndex: null,
      duplicateError: null,
    });
  };

  handlePlaceSelected = selectedPlace => {
    const { address, lat, lng, placesResults } = selectedPlace || {};
    const placesEntry = placesResults && placesResults[0];
    const placesName = placesEntry && placesEntry.name;

    if (!placesName && !address) return;

    this.setState(prev => {
      if (prev.pendingSelection) {
        return {
          pendingSelection: {
            ...prev.pendingSelection,
            address: address || prev.pendingSelection.address,
            lat: typeof lat === 'number' ? lat : prev.pendingSelection.lat,
            lng: typeof lng === 'number' ? lng : prev.pendingSelection.lng,
            placeId: (placesEntry && placesEntry.place_id) || prev.pendingSelection.placeId,
          },
        };
      }
      let name;
      if (placesName) {
        name = placesName;
      } else {
        name = address.split(',')[0].trim();
      }
      return {
        pendingSelection: {
          name,
          address: address || '',
          lat,
          lng,
          placeId: placesEntry ? placesEntry.place_id : undefined,
        },
      };
    });
  };

  handleNameChange = event => {
    const { value } = event.target;
    this.setState(prev =>
      prev.pendingSelection ? { pendingSelection: { ...prev.pendingSelection, name: value } } : null,
    );
  };

  handleConfirm = () => {
    const { value, onChange } = this.props;
    const { pendingSelection, editingIndex } = this.state;
    if (!pendingSelection || !pendingSelection.name || !pendingSelection.name.trim()) return;

    const trimmedName = pendingSelection.name.trim();
    const collisionIdx = value.findIndex(
      (s, i) => i !== editingIndex && s.location.toLowerCase() === trimmedName.toLowerCase(),
    );
    if (collisionIdx >= 0) {
      this.setState({ duplicateError: trimmedName });
      return;
    }

    const shelter = {
      location: trimmedName,
      coords:
        typeof pendingSelection.lat === 'number' && typeof pendingSelection.lng === 'number'
          ? [pendingSelection.lat, pendingSelection.lng]
          : undefined,
      address: pendingSelection.address || undefined,
      placeId: pendingSelection.placeId || undefined,
    };

    if (editingIndex !== null) {
      onChange(value.map((s, i) => (i === editingIndex ? shelter : s)));
    } else {
      onChange([...value, shelter]);
    }
    this.closePicker();
  };

  handleRemove = index => () => {
    const { value, onChange } = this.props;
    onChange(value.filter((_, i) => i !== index));
  };

  render() {
    const { value } = this.props;
    const { isDialogOpen, pendingSelection, editingIndex, duplicateError } = this.state;

    return (
      <Box className="shelter-list-manager" sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <strong>
            <IntlMessages id="diversion.composer.shelters.label" values={{ count: value.length }} />
          </strong>
          <Button
            size="small"
            color="primary"
            variant="contained"
            onClick={this.openAdd}
            data-testid="shelter-add-button"
          >
            <AddIcon sx={{ mr: 0.5 }} />
            <SimpleText id="diversion.composer.shelters.add" />
          </Button>
        </Box>

        {value.length > 0 && (
          <ul className={styles.shelterList}>
            {value.map((shelter, idx) => (
              <li
                // eslint-disable-next-line react/no-array-index-key
                key={`${shelter.location}-${idx}`}
                className={styles.shelterRow}
                data-testid="shelter-row"
              >
                <div className={styles.shelterBody}>
                  <div className={styles.shelterName}>{shelter.location}</div>
                  {shelter.address && (
                    <div className={styles.shelterAddress}>{shelter.address}</div>
                  )}
                </div>
                {Array.isArray(shelter.coords) &&
                  shelter.coords.length === 2 &&
                  typeof shelter.coords[0] === 'number' &&
                  typeof shelter.coords[1] === 'number' && (
                    <span className={styles.shelterCoordsChip}>
                      {`${shelter.coords[0].toFixed(4)}, ${shelter.coords[1].toFixed(4)}`}
                    </span>
                  )}
                <div className={styles.shelterEditCol}>
                  <IconButton
                    onClick={this.openEdit(idx)}
                    aria-label={`Edit ${shelter.location}`}
                    data-testid="shelter-edit-button"
                  >
                    <EditIcon />
                  </IconButton>
                </div>
                <div className={styles.shelterRemoveCol}>
                  <IconButton
                    onClick={this.handleRemove(idx)}
                    aria-label={`Remove ${shelter.location}`}
                    data-testid="shelter-remove-button"
                  >
                    <CloseIcon />
                  </IconButton>
                </div>
              </li>
            ))}
          </ul>
        )}

        <Dialog
          open={isDialogOpen}
          onClose={this.closePicker}
          maxWidth={false}
          fullWidth
          classes={{ paper: styles.pickerDialogPaper }}
          data-testid="shelter-picker-dialog"
        >
          <DialogTitle>
            <SimpleText
              id={
                editingIndex !== null
                  ? 'diversion.composer.shelters.picker.edit-title'
                  : 'diversion.composer.shelters.picker.add-title'
              }
            />
          </DialogTitle>
          <DialogContent>
            <MapPopupInfo
              withSearchBox
              addMarkerOnClick
              maxMarkersCount={1}
              markersDragAllowed
              defaultMarkersView
              recenterButton
              customZoomLevel={14}
              markers={
                pendingSelection && typeof pendingSelection.lat === 'number'
                  ? [{ position: { lat: pendingSelection.lat, lng: pendingSelection.lng } }]
                  : undefined
              }
              center={
                pendingSelection && typeof pendingSelection.lat === 'number'
                  ? { position: { lat: pendingSelection.lat, lng: pendingSelection.lng } }
                  : undefined
              }
              onPlaceSelected={this.handlePlaceSelected}
            />
            {pendingSelection ? (
              <div className={styles.previewCard} data-testid="shelter-picker-preview">
                <RoomIcon className={styles.previewIcon} />
                <div className={styles.previewBody}>
                  <TextField
                    fullWidth
                    value={pendingSelection.name}
                    onChange={this.handleNameChange}
                    slotProps={{
                      htmlInput: {
                        maxLength: 80,
                        'data-testid': 'shelter-name-input',
                      },
                    }}
                    margin="dense"
                    className={styles.previewName}
                  />
                  <div className={styles.previewAddress}>{pendingSelection.address}</div>
                </div>
              </div>
            ) : (
              <div className={styles.previewHelper} data-testid="shelter-picker-helper">
                <IntlMessages id="diversion.composer.shelters.picker.empty-helper" />
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {duplicateError && (
              <div
                className={styles.duplicateError}
                data-testid="shelter-duplicate-error"
              >
                <IntlMessages
                  id="diversion.composer.shelters.picker.duplicate-error"
                  values={{ name: duplicateError }}
                />
              </div>
            )}
            <Button
              onClick={this.closePicker}
              color="primary"
              data-testid="shelter-picker-cancel"
            >
              <SimpleText id="button.cancel" />
            </Button>
            <Button
              onClick={this.handleConfirm}
              color="primary"
              variant="contained"
              disabled={
                !pendingSelection ||
                !pendingSelection.name ||
                !pendingSelection.name.trim()
              }
              data-testid="shelter-picker-confirm"
            >
              <SimpleText
                id={
                  editingIndex !== null
                    ? 'diversion.composer.shelters.picker.save-confirm'
                    : 'diversion.composer.shelters.picker.add-confirm'
                }
              />
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

export default ShelterListManager;
